import React, { useMemo, useState, useReducer, useEffect } from 'react';
import { Trans, t } from '@lingui/macro';
import { ArrowLeftRight, Bell, Plus, Users } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { IconSparkles } from '../components/FigmaIcons';
import { ProfilePart } from './ProfilePart';
import { useAppUIContext } from '../../context/AppUIContext';
import { useHistory, useLocation } from '../../hooks/useRouterCompat';
import { MyNotifs } from '../../services/MyNotifs';
import { My } from '../../services/My';
import RoomItem from '../rooms/LeftMenuRoomItem';
import WorkspaceCreateModal from '../workspaces/WorkspaceCreateModal';
import WorkspaceJoinModal from '../workspaces/WorkspaceJoinModal';
import NotificationModal from '../main/NotificationModal';


export const LeftMenuHeader: React.FC = () => {
  const { workspaces, activeWorkspaceId, setIsMobileMenuOpen, rooms } = useAppUIContext();
  const history = useHistory();
  const location = useLocation();
  // 워크스페이스에 속하지 않는 legacy 개인 채널 ("내 채널 (구)") — 숨김 처리 안 한 것만
  const legacyPersonalRooms = useMemo(
    () => rooms.filter(r => r.visibility === 'personal' && (!r.workspaceId || r.workspaceId === 'null') && !My.RoomSettings.get(r.id)?.hideRoomInMyList),
    [rooms]
  );

  const [showCreateWSModal, setShowCreateWSModal] = useState(false);
  const [showJoinWSModal, setShowJoinWSModal] = useState(false);

  const [notifsByRoom] = useState(new Map<string, number>());
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    let unsubNotifs = MyNotifs.subscribeToNotificationUpdates((notifs) => {
      notifsByRoom.clear();
      MyNotifs.NotificationsByRoom.forEach((notifs, roomId) => {
        // 노출 대상(음소거·외부 API 비활성화 제외)만 카운트 — 통지센터·좌측 목록과 동일 기준
        const filteredCount = notifs.filter(n => n.isNew && n.type != 'RoomAdded' && n.type != 'CallStart' && MyNotifs.isVisibleInCenter(n, My.RoomSettings)).length;
        notifsByRoom.set(roomId, filteredCount);
      });
      forceUpdate();
    });
    return () => unsubNotifs();
  }, [notifsByRoom]);

  // 워크스페이스별 알림 카운트 계산
  const wsNotifCounts = useMemo(() => {
    const counts = new Map<string, number>();
    notifsByRoom.forEach((count, roomId) => {
      if (count <= 0) return;
      // mute(푸시 비활성화) 채널은 워크스페이스 배지에서 제외 — 통지벨 주황 배지와 일치.
      // notifsByRoom 자체는 좌측 legacy 채널 회색 배지에도 쓰이므로 여기(ws 카운트)에서만 필터.
      if (My.RoomSettings.get(roomId)?.disablePushNotifications) return;
      const room = rooms?.find(r => r.id === roomId);
      if (room?.workspaceId) {
        counts.set(room.workspaceId, (counts.get(room.workspaceId) || 0) + count);
      }
    });
    return counts;
  }, [rooms, _]); // _ (forceUpdate)로 notifsByRoom 변화 반영

  const otherWsNotifTotal = useMemo(() => {
    let total = 0;
    wsNotifCounts.forEach((count, wsId) => {
      if (wsId !== activeWorkspaceId) total += count;
    });
    return total;
  }, [wsNotifCounts, activeWorkspaceId]);

  return (
    <div className="shrink-0 px-4 pt-6 pb-0">
      <div className="mb-6">
        <ProfilePart />
      </div>

      {/* 워크스페이스 비소속 항목 (legacy 개인 채널 + AI 메모) — 워크스페이스 위에 좌우 배치 */}
      {legacyPersonalRooms.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-2">
          <div className="rounded-control border border-border/60 bg-muted min-[1200px]:bg-background min-w-0 overflow-hidden">
            {legacyPersonalRooms.map(room => (
              <RoomItem
                key={room.id}
                room={room}
                roomCall={undefined}
                nrOfNotifs={notifsByRoom.get(room.id) ?? 0}
                isSelected={location.pathname === `/channel/${room.id}`}
                theOtherUser={undefined}
                meInTheCall={false}
                pathEnd=""
                disabledPush={My.RoomSettings.get(room.id)?.disablePushNotifications ?? false}
                isLegacy={true}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => { history.push('/ai-memo'); if (window.innerWidth < 1200) setIsMobileMenuOpen(false); }}
            className={cn(
              "flex items-center gap-1.5 p-1.5 rounded-control border border-border/60 transition-colors cursor-pointer min-w-0",
              location.pathname.startsWith('/ai-memo') && !location.search.includes('roomId=')
                ? "bg-[rgba(255,127,0,0.2)] text-[color:rgb(var(--text-body))]"
                : "bg-muted min-[1200px]:bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
            )}
          >
            <IconSparkles className="w-4 h-4 text-purple-500 shrink-0" />
            <span className="text-body leading-[24px] font-normal truncate"><Trans>AI Memo</Trans></span>
            <span className="text-meta-sm text-muted-foreground ml-auto shrink-0">beta</span>
          </button>
        </div>
      )}

      {workspaces.length === 0 ? (
        <div className="mb-4 flex flex-col gap-2">
          <button
            onClick={() => setShowCreateWSModal(true)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-primary/5 hover:bg-primary/10 border border-primary/20 text-primary font-semibold text-sm transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <Trans>Create Workspace</Trans>
          </button>
          <button
            onClick={() => setShowJoinWSModal(true)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-muted hover:bg-accent border border-border text-[color:rgb(var(--text-body))] font-semibold text-sm transition-colors cursor-pointer"
          >
            <Users className="w-4 h-4" />
            <Trans>Join Workspace</Trans>
          </button>
        </div>
      ) : (
        <div className="mb-4">
          {/* 한 행에 두 가지 액션을 분리:
              - 좌측(워크스페이스 아이콘 + 이름): 클릭 시 WorkspaceViewPage(/ws/:id) 진입
              - 우측(⇄ 전환 버튼): 전체 워크스페이스 모달(WorkspaceSitemapModal) 열기 → 전환·채널검색·생성·가입 통합 */}
          <div className="w-full flex items-stretch rounded-xl border border-border/80 bg-muted min-[1200px]:bg-background shadow-[0_1px_2px_rgba(0,0,0,0.02)] overflow-hidden">
            <button
              type="button"
              onClick={() => {
                if (!activeWorkspaceId) return;
                history.push(`/ws/${activeWorkspaceId}`);
                if (window.innerWidth < 1200) setIsMobileMenuOpen(false);
              }}
              className="flex-1 min-w-0 flex items-center gap-2.5 px-3 py-2.5 hover:bg-accent transition-colors cursor-pointer text-left"
              title={t`Open workspace`}
            >
              {(() => {
                const aws = workspaces.find(w => w.id === activeWorkspaceId);
                return aws?.iconUrl ? (
                  <img src={aws.iconUrl} className="w-8 h-8 rounded-control object-cover shrink-0 border border-border/50" alt="" />
                ) : (
                  <div className="w-8 h-8 rounded-control bg-[rgba(255,127,0,0.1)] text-primary flex items-center justify-center font-bold text-body shrink-0 border border-[rgba(255,127,0,0.1)]">
                    {aws?.name?.charAt(0) || "W"}
                  </div>
                );
              })()}
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <p className="text-meta-sm leading-[12px] text-muted-foreground font-semibold uppercase tracking-wider mb-0.5"><Trans>Workspace</Trans></p>
                <p className="text-body leading-[18px] font-bold text-[color:rgb(var(--text-body))] truncate">
                  {workspaces.find(w => w.id === activeWorkspaceId)?.name || t`Select Workspace`}
                </p>
              </div>
            </button>
            <div className="w-px bg-border/60" aria-hidden />
            {/* 워크스페이스 스코프 통지 벨 — 전환 버튼 왼쪽. 워크스페이스 2개 이상일 때만 (1개면 프로필 벨과 중복이라 불필요) */}
            {workspaces.length > 1 && (
              <>
                <NotificationModal
                  workspaceId={activeWorkspaceId}
                  renderTrigger={(count) => (
                    <button
                      type="button"
                      className="relative inline-flex items-center px-2.5 hover:bg-accent transition-colors cursor-pointer shrink-0 group"
                      title={t`Notifications`}
                    >
                      <span className="relative inline-flex">
                        <Bell className="w-3.5 h-3.5 text-muted-foreground group-hover:text-[color:rgb(var(--text-body))] transition-colors" />
                        {count > 0 && (
                          <Badge variant="notification" className="absolute -top-1.5 -right-2 scale-[0.8]">
                            {count > 99 ? '99+' : count}
                          </Badge>
                        )}
                      </span>
                    </button>
                  )}
                />
                <div className="w-px bg-border/60" aria-hidden />
              </>
            )}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event('open-workspace-sitemap'))}
              className="relative inline-flex items-center gap-1 px-2.5 hover:bg-accent transition-colors cursor-pointer shrink-0 group"
              title={t`Switch workspace`}
            >
              <span className="relative inline-flex">
                <ArrowLeftRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-[color:rgb(var(--text-body))] transition-colors" />
                {otherWsNotifTotal > 0 && (
                  <span className="absolute -top-2 -right-2 size-1.5 rounded-full bg-primary" aria-label="unread" />
                )}
              </span>
              <span className="text-meta-sm font-semibold text-muted-foreground group-hover:text-[color:rgb(var(--text-body))] transition-colors">
                <Trans>Switch</Trans>
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Workspace Modals */}
      {showCreateWSModal && <WorkspaceCreateModal show={showCreateWSModal} setShow={setShowCreateWSModal} />}
      {showJoinWSModal && <WorkspaceJoinModal show={showJoinWSModal} setShow={setShowJoinWSModal} />}
    </div>
  );
};
